import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import AuthLayout from "@/components/auth/AuthLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API, formatApiError, useAuth } from "@/contexts/AuthContext";

// If a /website cart-checkout was started before login, the resume flag
// lives in sessionStorage under this key. If present, we OVERRIDE the URL
// return-target so the user always lands back in their cart — no matter
// how the `?return=` param was escaped or lost along the way.
const CART_RESUME_KEY = "zynthoro_wd_resume_flow";
// Same mechanism for Kickstart CTAs: clicking "Choose Kickstart X" on
// /pricing while logged out stashes the tier key here so the user is
// routed to /subscribe/kickstart_X after signup+verify+login+2FA
// instead of being dropped on /dashboard with a trial banner.
const KICKSTART_INTENT_KEY = "zynthoro_kickstart_intent";

// Only allow same-origin relative paths as return targets (prevent open redirects).
const safeReturn = (r) => {
  try {
    if (sessionStorage.getItem(CART_RESUME_KEY) === "1") return "/website?resume=1";
    const k = localStorage.getItem(KICKSTART_INTENT_KEY);
    if (k && /^kickstart_[123]$/.test(k)) return `/subscribe/${k}`;
  } catch { /* ignore */ }
  return r && r.startsWith("/") && !r.startsWith("//") ? r : "/dashboard";
};

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = safeReturn(searchParams.get("return"));
  const { refresh } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // HeyCatch c_duplicate_titles — ensure /login has a unique page title
  // and meta description at runtime, independent of the prerendered shell.
  useEffect(() => {
    document.title = "Log in to Zynthoro — Access your workspace";
    let desc = document.querySelector('meta[name="description"]');
    if (!desc) {
      desc = document.createElement("meta");
      desc.setAttribute("name", "description");
      document.head.appendChild(desc);
    }
    desc.setAttribute(
      "content",
      "Log in to your Zynthoro workspace to continue with the AI-native business platform. Password + two-factor supported."
    );
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, form);
      if (data.stage === "2fa_setup_required") {
        navigate("/2fa/setup", { state: { pre_token: data.pre_token, email: form.email, returnTo } });
      } else if (data.stage === "2fa_required") {
        navigate("/2fa/verify", {
          state: {
            pre_token: data.pre_token,
            email: form.email,
            available_methods: data.available_methods,
            primary_method: data.twofa_method,
            returnTo,
          },
        });
      } else {
        await refresh();
        navigate(returnTo);
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const signupHref = returnTo === "/dashboard" ? "/signup" : `/signup?return=${encodeURIComponent(returnTo)}`;

  return (
    <AuthLayout title="Welcome back" subtitle="Log in to your Zynthoro workspace.">
      <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
        <div className="space-y-1.5">
          <Label htmlFor="em" className="text-[13px] font-medium">Email address</Label>
          <Input id="em" type="email" data-testid="login-email" value={form.email} onChange={onChange("email")} required />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="pw" className="text-[13px] font-medium">Password</Label>
            <Link to="/forgot-password" className="text-[12.5px] text-[#1A4FFF] font-medium">Forgot password?</Link>
          </div>
          <Input id="pw" type="password" data-testid="login-password" value={form.password} onChange={onChange("password")} required />
        </div>

        <button type="submit" data-testid="login-submit" disabled={submitting} className="zy-btn-primary w-full disabled:opacity-70">
          {submitting ? "Logging in…" : "Log In"}
        </button>

        <p className="text-[13px] text-center text-[#555]">
          Don&apos;t have an account?{" "}
          <Link to={signupHref} className="text-[#1A4FFF] font-semibold">Sign up</Link>
        </p>
        <p className="text-[12px] text-center text-[#888] mt-1">
          Need help? <a href="mailto:support@zynthoro.ai" className="text-[#1A4FFF] font-medium">support@zynthoro.ai</a>
        </p>
      </form>
    </AuthLayout>
  );
}
